import { motion } from "framer-motion";
import { AlertTriangle, Wrench, TrendingUp } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const CASES = [
  {
    img: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg",
    tags: ["Proxmox", "ZFS", "PBS"],
    ro: {
      title: "Private Cloud pentru o firmă de producție",
      challenge: "Servere vechi, fără backup testat și downtime frecvent care oprea producția.",
      solution: "Cluster Proxmox cu storage ZFS, backup automat pe Proxmox Backup Server și migrare live între noduri — fără întreruperi.",
      results: [{ v: "99.9%", l: "uptime" }, { v: "<5 min", l: "timp restore" }, { v: "−40%", l: "costuri hardware" }],
    },
    en: {
      title: "Private Cloud for a manufacturing company",
      challenge: "Aging servers, no tested backup and frequent downtime halting production.",
      solution: "Proxmox cluster with ZFS storage, automated Proxmox Backup Server and live migration between nodes — zero downtime.",
      results: [{ v: "99.9%", l: "uptime" }, { v: "<5 min", l: "restore time" }, { v: "−40%", l: "hardware cost" }],
    },
  },
  {
    img: "https://images.unsplash.com/photo-1562575214-da9fcf59b907",
    tags: ["pfSense", "VPN", "Multi-WAN"],
    ro: {
      title: "Securizare multi-sediu pentru retail",
      challenge: "Sedii neconectate, fără VPN, cu risc de securitate și fără redundanță de internet.",
      solution: "pfSense cu VPN site-to-site, segmentare VLAN și multi-WAN cu failover automat între sedii.",
      results: [{ v: "3", l: "sedii unite" }, { v: "0", l: "breșe" }, { v: "24/7", l: "failover activ" }],
    },
    en: {
      title: "Multi-site security for retail",
      challenge: "Disconnected sites, no VPN, security risks and no internet redundancy.",
      solution: "pfSense with site-to-site VPN, VLAN segmentation and multi-WAN with automatic failover.",
      results: [{ v: "3", l: "sites linked" }, { v: "0", l: "breaches" }, { v: "24/7", l: "active failover" }],
    },
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/c74574e6-69b0-4d56-80d7-aa66fb56a651/images/16460015d921e8886f99644345692bb22a69e4c189b6bcdb290b8f3b80b4023c.jpeg",
    tags: ["ERP", "Business Events", "API"],
    ro: {
      title: "Platformă ERP & automatizări",
      challenge: "Rapoarte făcute manual, date nesincronizate între sisteme și procese lente.",
      solution: "Generic Inquiries, Business Events și integrări API care sincronizează datele automat, în timp real.",
      results: [{ v: "−70%", l: "timp raportare" }, { v: "real-time", l: "sincronizare" }, { v: "100%", l: "date corecte" }],
    },
    en: {
      title: "ERP platform & automations",
      challenge: "Manual reporting, unsynced data across systems and slow processes.",
      solution: "Generic Inquiries, Business Events and API integrations that sync data automatically, in real time.",
      results: [{ v: "−70%", l: "reporting time" }, { v: "real-time", l: "sync" }, { v: "100%", l: "data accuracy" }],
    },
  },
];

const EASE = [0.22, 1, 0.36, 1];

export const CaseStudies = () => {
  const { lang, t } = useI18n();
  return (
    <section id="studii" data-testid="cases-section" className="relative border-t border-white/10 bg-ink-800">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ • ]">{t("cases.eyebrow")}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("cases.t1")} <span className="text-aurora">{t("cases.t2")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">{t("cases.sub")}</p>
          </Reveal>
        </div>

        <div className="mt-16 flex flex-col gap-16 sm:gap-24">
          {CASES.map((c, i) => {
            const d = c[lang] || c.ro;
            const flip = i % 2 === 1;
            return (
              <motion.article
                key={i}
                data-testid={`case-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: EASE }}
                className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14"
              >
                <div className={`group relative overflow-hidden rounded-2xl border border-white/10 ${flip ? "lg:order-2" : ""}`}>
                  <img src={c.img} alt={d.title} className="h-64 w-full object-cover opacity-70 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-90 group-hover:grayscale-0 sm:h-80 lg:h-[26rem]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 to-transparent" />
                  <div className="absolute left-5 top-5 flex flex-wrap gap-2">
                    {c.tags.map((tg) => (
                      <span key={tg} className="rounded border border-cyan-neon/30 bg-ink-900/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-cyan-neon backdrop-blur-md">{tg}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{d.title}</h3>

                  <div className="mt-6 space-y-4">
                    <div className="flex gap-3">
                      <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#ff7a97]" />
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("cases.challenge")}</div>
                        <p className="mt-1 font-body text-sm leading-relaxed text-white/60">{d.challenge}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Wrench size={18} className="mt-0.5 shrink-0 text-cyan-neon" />
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("cases.solution")}</div>
                        <p className="mt-1 font-body text-sm leading-relaxed text-white/60">{d.solution}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                    <TrendingUp size={14} /> {t("cases.results")}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10">
                    {d.results.map((r) => (
                      <div key={r.l} className="bg-ink-900 p-4 text-center">
                        <div className="font-display text-xl font-extrabold text-cyan-neon sm:text-2xl">{r.v}</div>
                        <div className="mt-1 font-mono text-[9px] uppercase tracking-wider text-white/40">{r.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
